import { makeAutoObservable, runInAction } from "mobx";
import { track } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type { IWorkspaceMemberInvitation } from "@/types/workspace";

export class InvitationStore {
  invitations: IWorkspaceMemberInvitation[] = [];
  isLoading = false;
  // A failed load must not read as "you have no invitations" — the page renders
  // a distinct error state off this flag (#3819).
  loadFailed = false;
  selectedIds: Set<string> = new Set();

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  get pendingInvitations(): IWorkspaceMemberInvitation[] {
    return this.invitations.filter((inv) => !inv.accepted && !inv.responded_at);
  }

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  toggleSelection(id: string): void {
    if (this.selectedIds.has(id)) {
      this.selectedIds.delete(id);
    } else {
      this.selectedIds.add(id);
    }
  }

  selectAll(): void {
    this.pendingInvitations.forEach((inv) => this.selectedIds.add(inv.id));
  }

  clearSelection(): void {
    this.selectedIds.clear();
  }

  // Handles its own failure rather than throwing: the only caller is the
  // /invitations route loader, and a load failure is a UI state here (the error
  // panel), not a route-level crash. Logged with context so a real outage is
  // diagnosable instead of looking like an empty invitation list (#3819).
  async fetchInvitations(): Promise<void> {
    this.isLoading = true;
    this.loadFailed = false;
    try {
      const invitations = await this.rootStore.invitationService.fetchMyInvitations();
      runInAction(() => {
        this.invitations = invitations;
      });
    } catch (error) {
      console.error("[invitations] failed to load the current user's invitations", error);
      runInAction(() => {
        this.loadFailed = true;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async acceptSelected(): Promise<string | null> {
    const ids = Array.from(this.selectedIds);
    if (ids.length === 0) return null;

    await this.rootStore.invitationService.acceptInvitations({ invitations: ids });
    track("workspace_member_joined", { count: ids.length });

    const firstAccepted = this.invitations.find((inv) => ids.includes(inv.id));
    const redirectSlug = firstAccepted?.workspace.slug ?? null;

    runInAction(() => {
      this.invitations = this.invitations.filter((inv) => !ids.includes(inv.id));
      this.selectedIds.clear();
    });

    await this.rootStore.workspace.fetchWorkspaces();

    return redirectSlug;
  }
}
